import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastService } from '../../../services/toast.service';
import { LoadingService } from '../../../services/loading.service';
import { BankReconciliationService, BankReconciliationRequestDTO, BankReconciliationType, PendingReceiptsParams } from '../../../services/bank-reconciliation.service';
import { BanksService } from '../../../services/banks.service';
import { ParticipantService } from '../../../services/participant.service';
import { ProjectService } from '../../../services/project.service';
import { CompanyService } from '../../../services/company.service';
import { BankClosingService } from '../../../services/bank-closing.service';
import { FeeTypesService } from '../../../services/fee-types.service'; // 🆕 Para fees
import { forkJoin } from 'rxjs';
import { BaseFormComponent } from '../../shared/base-form/base-form.component';
import { ConfirmationService } from '../../../services/confirmation.service';
import { BatchConfigOptions } from '../batch-config-modal/batch-config-modal.component';

@Component({
  selector: 'app-new-bank-reconciliation',
  templateUrl: './new-bank-reconciliation.component.html',
  styleUrl: './new-bank-reconciliation.component.scss'
})
export class NewBankReconciliationComponent extends BaseFormComponent implements OnInit {

  // === PROPIEDADES DEL FORMULARIO ===
  reconciliationForm!: FormGroup;

  // === PROPIEDADES DEL WIZARD ===
  step: number = 1;
  totalSteps: number = 5;

  // === DROPDOWNS DATA ===
  banks: any[] = [];
  projects: any[] = [];
  participants: any[] = [];
  companies: any[] = [];
  feeTypes: any[] = []; // 🆕 Para fees/descuentos

  // === COMPROBANTES PENDIENTES ===
  pendingIncomeReceipts: any[] = [];
  pendingExpenseVouchers: any[] = [];
  selectedReceipts: any[] = [];

  // === FORMULARIOS PARA COMPROBANTES SELECCIONADOS ===
  selectedReceiptsForm!: FormGroup; // FormGroup que contiene FormArray de comprobantes seleccionados

  // === CONFIGURACIÓN ===
  reconciliationType: BankReconciliationType = BankReconciliationType.INCOME;

  // === RESUMEN DE CONCILIACIÓN ===
  reconciliationSummary = {
    totalOriginal: 0,
    totalBank: 0,
    totalSelected: 0,
    selectedCount: 0,
    differenceAmount: 0,
    differencePercentage: 0
  };

  // === AUTO-RECONCILIACIÓN ===
  autoReconcileResults: any = null;
  showAutoReconcileResults: boolean = false;

  // === CONCILIACIÓN COMPLETADA ===
  completedReconciliation: any = null;
  isReconciliationCompleted: boolean = false;

  // === 🆕 NUEVAS PROPIEDADES PARA MODALES ===
  showReceiptConfigModal: boolean = false;
  showBatchConfigModal: boolean = false;
  currentReceiptIndex: number = -1;
  currentReceiptForModal: any = null;

  // === BANK CLOSING PROPERTY ===
  isPeriodClosed: boolean = false;

  // === ENUMS PARA EL TEMPLATE ===
  ReconciliationType = BankReconciliationType;

  // === UTILIDADES PARA EL TEMPLATE ===
  Math = Math;
  FormGroup = FormGroup; // Para usar en el template

  // === 🆕 VIEW MODE PROPERTIES ===
  isViewMode: boolean = false;
  reconciliationId: number | null = null;
  existingReconciliation: any = null;

  constructor(
    private fb: FormBuilder,
    router: Router,
    private route: ActivatedRoute,
    private toastService: ToastService,
    private loadingService: LoadingService,
    private bankReconciliationService: BankReconciliationService,
    private bankClosingService: BankClosingService,
    private banksService: BanksService,
    private participantService: ParticipantService,
    private projectService: ProjectService,
    private companyService: CompanyService,
    private feeTypesService: FeeTypesService, // 🆕 Para fees
    confirmationService: ConfirmationService
  ) {
    super(confirmationService, router);
  }

  // === MÉTODOS REQUERIDOS POR BaseFormComponent ===

  protected override initializeForm(): void {
    this.reconciliationForm = this.fb.group({
      bankId: [null, Validators.required],
      reconciliationType: [BankReconciliationType.INCOME, Validators.required],
      month: [new Date().getMonth() + 1, [Validators.required, Validators.min(1), Validators.max(12)]],
      year: [new Date().getFullYear(), [Validators.required, Validators.min(2020), Validators.max(2030)]],
      totalBankAmount: [0, [Validators.required, Validators.min(0.01)]],
      notes: [''],

      // Filtros opcionales
      projectId: [null],
      participantId: [null],
      companyId: [null],
      minAmount: [null],
      maxAmount: [null],
      autoReconciliationTolerance: [0.01, [Validators.min(0.001), Validators.max(100)]]
    });

    // 🆕 FormGroup para comprobantes seleccionados con montos y fees individuales
    this.selectedReceiptsForm = this.fb.group({
      selectedReceiptsArray: this.fb.array([]) // FormArray de comprobantes con sus montos y fees
    });
  }

  protected override getMainForm(): FormGroup {
    return this.reconciliationForm;
  }

  public override getFormName(): string {
    return 'conciliación bancaria';
  }

  public override getCriticalData(): string[] {
    const data: string[] = [];

    // Información del banco
    const bankId = this.reconciliationForm.get('bankId')?.value;
    const bank = this.banks.find(b => b.value === bankId);
    if (bank) {
      data.push(`Banco: ${bank.label}`);
    }

    // Tipo de conciliación
    const type = this.reconciliationForm.get('reconciliationType')?.value;
    data.push(`Tipo: ${this.getReconciliationTypeDisplayName(type)}`);

    // Período
    const periodFrom = this.reconciliationForm.get('periodFrom')?.value;
    const periodTo = this.reconciliationForm.get('periodTo')?.value;
    if (periodFrom && periodTo) {
      data.push(`Período: ${periodFrom} - ${periodTo}`);
    }

    // Monto del banco
    const bankAmount = this.reconciliationForm.get('totalBankAmount')?.value;
    if (bankAmount) {
      data.push(`Monto Banco: ${this.formatCurrency(bankAmount)}`);
    }

    // Comprobantes seleccionados
    if (this.selectedReceipts.length > 0) {
      data.push(`Comprobantes: ${this.selectedReceipts.length} seleccionados`);
    }

    return data;
  }

  protected override async performFormSubmit(): Promise<void> {
    await this.createReconciliation();
  }

  protected override navigateBack(): void {
    this.router.navigate(['/bank-reconciliation']);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeForm();

    // 🆕 Check if we're in view mode (has ID parameter)
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.reconciliationId = +params['id'];
        this.isViewMode = true;
        this.loadExistingReconciliation();
      } else {
        this.isViewMode = false;
        this.loadInitialData();
      }
    });
  }

  // === CARGA DE DATOS INICIALES ===

  loadInitialData(): void {
    this.loadingService.show();
    forkJoin({
      banks: this.banksService.getAll(),
      projects: this.projectService.getLookup(),   // 🆕 antes: getAll()
      participants: this.participantService.getAll(),
      companies: this.companyService.getAll(),
      feeTypes: this.feeTypesService.getAll() // 🆕 Cargar tipos de fees
    }).subscribe({
      next: (results) => {
        // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
        const banksData = Array.isArray(results.banks.data) ? results.banks.data : (results.banks.data?.data || []);
        
        this.banks = banksData.map((bank: any) => ({
          label: `${bank.bankName} - ${bank.accountNumber}`,
          value: bank.id,
          data: bank
        }));

        // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
        const projectsData = Array.isArray(results.projects.data) ? results.projects.data : (results.projects.data?.data || []);
        const participantsData = Array.isArray(results.participants.data) ? results.participants.data : (results.participants.data?.data || []);
        const companiesData = Array.isArray(results.companies.data) ? results.companies.data : (results.companies.data?.data || []);
        // feeTypes siempre devuelve un array directamente en data
        const feeTypesData = results.feeTypes.data || [];
        
        this.projects = projectsData.map((project: any) => ({
          label: `${project.projectId} - ${project.name}`,
          value: project.id,
          data: project
        }));

        this.participants = participantsData.map((participant: any) => ({
          label: `${participant.documentId} - ${participant.firstName} ${participant.lastName}`,
          value: participant.id,
          data: participant
        }));

        this.companies = companiesData.map((company: any) => ({
          label: `${company.documentId} - ${company.name}`,
          value: company.id,
          data: company
        }));

        this.feeTypes = feeTypesData; // 🆕 Mapear fee types

        this.loadingService.hide();
      },
      error: (error) => {
        console.error('Error loading initial data:', error);
        this.toastService.showToast('error', 'Error cargando datos iniciales');
        this.loadingService.hide();
      }
    });
  }

  // === 🆕 LOAD EXISTING RECONCILIATION FOR VIEW MODE ===

  async loadExistingReconciliation(): Promise<void> {
    if (!this.reconciliationId) return;
    this.loadingService.show();
    try {
      // 1. Cargar datos maestros por si se necesitan formateadores
      await this.loadInitialDataSync();

      // 2. Cargar datos de la conciliación
      const response = await this.bankReconciliationService.getReconciliationSummary(this.reconciliationId, true).toPromise();

      if (response?.isSuccess) {
        this.existingReconciliation = response.data;
        this.isViewMode = true;

        // Si la conciliación está completada, podemos usar el objeto para el reporte
        if (this.existingReconciliation.status === 'COMPLETED') {
          this.completedReconciliation = this.existingReconciliation;
          this.isReconciliationCompleted = true;
        }
      } else {
        this.toastService.showToast('error', response?.errorMessage || 'Error cargando datos de la conciliación');
        this.router.navigate(['/bank-reconciliation']);
      }
    } catch (error) {
      console.error('Error loading existing reconciliation:', error);
      this.toastService.showToast('error', 'Error cargando conciliación');
      this.router.navigate(['/bank-reconciliation']);
    } finally {
      this.loadingService.hide();
    }
  }

  private async loadInitialDataSync(): Promise<void> {
    return new Promise((resolve, reject) => {
      forkJoin({
        banks: this.banksService.getAll(),
        projects: this.projectService.getLookup(),   // 🆕 antes: getAll()
        participants: this.participantService.getAll(),
        companies: this.companyService.getAll(),
        feeTypes: this.feeTypesService.getAll()
      }).subscribe({
        next: (results) => {
          // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
          const banksData = Array.isArray(results.banks.data) ? results.banks.data : (results.banks.data?.data || []);
          
          this.banks = banksData.map((bank: any) => ({
            label: `${bank.bankName} - ${bank.accountNumber}`,
            value: bank.id,
            data: bank
          }));
          // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
          const projectsData = Array.isArray(results.projects.data) ? results.projects.data : (results.projects.data?.data || []);
          const participantsData = Array.isArray(results.participants.data) ? results.participants.data : (results.participants.data?.data || []);
          const companiesData = Array.isArray(results.companies.data) ? results.companies.data : (results.companies.data?.data || []);
          // feeTypes siempre devuelve un array directamente en data
          const feeTypesData = results.feeTypes.data || [];
          
          this.projects = projectsData.map((project: any) => ({
            label: `${project.projectId} - ${project.name}`,
            value: project.id,
            data: project
          }));
          this.participants = participantsData.map((participant: any) => ({
            label: `${participant.documentId} - ${participant.firstName} ${participant.lastName}`,
            value: participant.id,
            data: participant
          }));
          this.companies = companiesData.map((company: any) => ({
            label: `${company.documentId} - ${company.name}`,
            value: company.id,
            data: company
          }));
          this.feeTypes = feeTypesData;
          resolve();
        },
        error: (error) => {
          console.error('Error loading initial data:', error);
          reject(error);
        }
      });
    });
  }

  // === MÉTODOS DEL WIZARD ===

  /**
   * Navegar a un paso específico del wizard
   */
  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) {
      this.step = step;
    }
  }

  // === MÉTODO ONSUBMIT REQUERIDO POR EL FORM ===

  async onSubmit(): Promise<void> {
    await this.handleFormSubmit();
  }

  /**
   * Validar si el paso actual es válido
   */
  isCurrentStepValid(): boolean {
    switch (this.step) {
      case 1:
        return this.isStep1Valid();
      case 2:
        return this.isStep2Valid();
      case 3:
        return this.isStep3Valid();
      case 4:
        return this.isStep4Valid();
      case 5:
        return this.isStep5Valid();
      default:
        return false;
    }
  }

  /**
   * Validar Step 1: Configuración Básica
   */
  isStep1Valid(): boolean {
    return !!(
      this.reconciliationForm.get('bankId')?.valid &&
      this.reconciliationForm.get('reconciliationType')?.valid &&
      this.reconciliationForm.get('month')?.valid &&
      this.reconciliationForm.get('year')?.valid &&
      this.reconciliationForm.get('totalBankAmount')?.valid &&
      this.isValidPeriod()
    );
  }

  /**
   * Validar Step 2: Carga de Comprobantes
   */
  private isStep2Valid(): boolean {
    return this.pendingIncomeReceipts.length > 0 || this.pendingExpenseVouchers.length > 0;
  }

  /**
   * Validar Step 3: Selección de Comprobantes
   */
  isStep3Valid(): boolean {
    return this.selectedReceipts.length > 0;
  }

  /**
   * Validar Step 4: Conciliación
   */
  private isStep4Valid(): boolean {
    return this.reconciliationSummary.selectedCount > 0;
  }

  /**
   * Validar Step 5: Finalización
   */
  private isStep5Valid(): boolean {
    return this.isReconciliationCompleted;
  }

  /**
   * Validar que el período de fechas sea válido
   */
  isValidPeriod(): boolean {
    const month = this.reconciliationForm.get('month')?.value;
    const year = this.reconciliationForm.get('year')?.value;

    if (!month || !year) return false;

    // Validar rangos
    if (month < 1 || month > 12) return false;
    if (year < 2020 || year > new Date().getFullYear()) return false;

    // Validar que no sea un mes futuro
    const targetDate = new Date(year, month - 1, 1); // month - 1 porque Date usa 0-based months
    const currentDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

    return targetDate <= currentDate;
  }

  // === CHECK PERIOD VISIBILITY ===

  async checkPeriodStatus(): Promise<void> {
    const bankId = this.reconciliationForm.get('bankId')?.value;
    const month = this.reconciliationForm.get('month')?.value;
    const year = this.reconciliationForm.get('year')?.value;

    if (bankId && month && year) {
      try {
        const response = await this.bankClosingService.checkStatus(bankId, year, month).toPromise();
        if (response && response.isSuccess) {
          this.isPeriodClosed = !!response.data;
          if (this.isPeriodClosed) {
            this.toastService.showToast('warning', 'El periodo seleccionado se encuentra cerrado.');
          }
        }
      } catch (error) {
        console.error('Error checking period status:', error);
      }
    } else {
      this.isPeriodClosed = false;
    }
  }

  // === STEP 1: CONFIGURACIÓN BÁSICA ===

  onReconciliationTypeChange(type: BankReconciliationType): void {
    this.reconciliationType = type;
    this.reconciliationForm.patchValue({ reconciliationType: type });

    // Limpiar datos dependientes
    this.clearDependentData();
  }

  onBankChange(): void {
    // Limpiar datos dependientes cuando cambia el banco
    this.clearDependentData();
    this.checkPeriodStatus();
  }

  onMonthYearChange(): void {
    // Limpiar datos dependientes cuando cambia el mes o año
    this.clearDependentData();
    this.checkPeriodStatus();
  }

  private clearDependentData(): void {
    this.pendingIncomeReceipts = [];
    this.pendingExpenseVouchers = [];
    this.selectedReceipts = [];
    this.autoReconcileResults = null;
    this.showAutoReconcileResults = false;
    this.updateSummary();
  }

  // === STEP 2: CARGA DE COMPROBANTES ===

  async loadPendingReceipts(): Promise<void> {
    if (!this.isStep1Valid()) {
      this.toastService.showToast('warning', 'Complete la configuración básica primero');
      return;
    }

    if (this.isPeriodClosed) {
      this.toastService.showToast('error', 'El periodo seleccionado está cerrado. No se puede continuar.');
      return;
    }

    this.loadingService.show();

    try {
      const params: PendingReceiptsParams = {
        bankId: this.reconciliationForm.get('bankId')?.value,
        month: this.reconciliationForm.get('month')?.value,
        year: this.reconciliationForm.get('year')?.value,
        projectId: this.reconciliationForm.get('projectId')?.value || undefined,
        participantId: this.reconciliationForm.get('participantId')?.value || undefined,
        companyId: this.reconciliationForm.get('companyId')?.value || undefined,
        minAmount: this.reconciliationForm.get('minAmount')?.value || undefined,
        maxAmount: this.reconciliationForm.get('maxAmount')?.value || undefined
      };

      if (this.reconciliationType === BankReconciliationType.INCOME) {
        const response = await this.bankReconciliationService.getPendingIncomeReceipts(params).toPromise();
        if (response?.isSuccess) {
          this.pendingIncomeReceipts = response.data || [];
          this.pendingExpenseVouchers = [];
        }
      } else if (this.reconciliationType === BankReconciliationType.EXPENSE) {
        const response = await this.bankReconciliationService.getPendingExpenseVouchers(params).toPromise();
        if (response?.isSuccess) {
          this.pendingExpenseVouchers = response.data || [];
          this.pendingIncomeReceipts = [];
        }
      } else if (this.reconciliationType === BankReconciliationType.BOTH) {
        const response = await this.bankReconciliationService.getAllPendingReceipts(params).toPromise();
        if (response?.isSuccess) {
          this.pendingIncomeReceipts = response.data?.incomeReceipts || [];
          this.pendingExpenseVouchers = response.data?.expenseVouchers || [];
        }
      }

      // Avanzar automáticamente al paso 3 si se cargaron comprobantes
      if (this.pendingIncomeReceipts.length > 0 || this.pendingExpenseVouchers.length > 0) {
        this.toastService.showToast('success',
          `${this.pendingIncomeReceipts.length + this.pendingExpenseVouchers.length} comprobantes cargados`);
        this.goToStep(3);
      } else {
        this.toastService.showToast('warning', 'No se encontraron comprobantes pendientes para el período seleccionado');
      }

    } catch (error) {
      console.error('Error loading pending receipts:', error);
      this.toastService.showToast('error', 'Error cargando comprobantes pendientes');
    } finally {
      this.loadingService.hide();
    }
  }

  // === STEP 3: SELECCIÓN DE COMPROBANTES ===

  toggleReceiptSelection(receipt: any, type: 'income' | 'expense'): void {
    const receiptWithType = { ...receipt, type };
    const index = this.selectedReceipts.findIndex(r =>
      r.id === receipt.id && r.type === type
    );

    if (index > -1) {
      // Quitar de selección
      this.selectedReceipts.splice(index, 1);
      this.removeReceiptFromForm(receipt.id, type);
    } else {
      // Agregar a selección
      this.selectedReceipts.push(receiptWithType);
      this.addReceiptToForm(receiptWithType);
    }

    this.updateSummary();
  }

  // === MÉTODOS PARA FORMARRAY DE COMPROBANTES SELECCIONADOS ===

  get selectedReceiptsArray(): FormArray {
    return this.selectedReceiptsForm.get('selectedReceiptsArray') as FormArray;
  }

  /**
   * Agrega un comprobante al FormArray con su estructura de fees
   */
  addReceiptToForm(receipt: any): void {
    const receiptGroup = this.fb.group({
      id: [receipt.id],
      type: [receipt.type],
      receiptData: [receipt],
      bankAmount: [receipt.type === 'income' ? receipt.netAmount : receipt.totalToPay, [Validators.required, Validators.min(0.01)]],
      adjustmentReason: [''],
      notes: [''],
      feeDetails: this.fb.array([]) // FormArray de fees para este comprobante
    });

    this.selectedReceiptsArray.push(receiptGroup);
  }

  /**
   * Remueve un comprobante del FormArray
   */
  removeReceiptFromForm(receiptId: number, type: string): void {
    const index = this.selectedReceiptsArray.controls.findIndex((control: any) => {
      const group = control as FormGroup;
      return group.get('id')?.value === receiptId && group.get('type')?.value === type;
    });

    if (index > -1) {
      this.selectedReceiptsArray.removeAt(index);
    }
  }

  /**
   * Obtiene el FormArray de fees para un comprobante específico
   */
  getReceiptFeeDetails(receiptIndex: number): FormArray {
    const receiptGroup = this.selectedReceiptsArray.at(receiptIndex) as FormGroup;
    return receiptGroup.get('feeDetails') as FormArray;
  }

  /**
   * Agrega un fee a un comprobante específico (siguiendo patrón de income-receipt)
   */
  addFeeToReceipt(receiptIndex: number): void {
    const feeGroup = this.fb.group({
      feeTypeId: ['', Validators.required],
      requestAmount: ['', [Validators.required]],
      description: [''],
      amount: [0], // Se calculará automáticamente
      porcentualAmount: [0] // Se calculará automáticamente
    });

    this.getReceiptFeeDetails(receiptIndex).push(feeGroup);
  }

  /**
   * Remueve un fee de un comprobante específico
   */
  removeFeeFromReceipt(receiptIndex: number, feeIndex: number): void {
    this.getReceiptFeeDetails(receiptIndex).removeAt(feeIndex);
    this.calculateReceiptTotals(receiptIndex);
  }

  /**
   * Calcula totales cuando cambia un fee (siguiendo lógica de income-receipt)
   */
  onFeeChange(receiptIndex: number, feeIndex: number): void {
    this.calculateFeeAmount(receiptIndex, feeIndex);
    this.calculateReceiptTotals(receiptIndex);
  }

  /**
   * Calcula el monto de un fee específico
   */
  calculateFeeAmount(receiptIndex: number, feeIndex: number): void {
    const receiptGroup = this.selectedReceiptsArray.at(receiptIndex) as FormGroup;
    const feeGroup = this.getReceiptFeeDetails(receiptIndex).at(feeIndex) as FormGroup;

    const requestAmount = feeGroup.get('requestAmount')?.value;
    const receiptData = receiptGroup.get('receiptData')?.value;
    const originalAmount = receiptData.type === 'income' ? receiptData.netAmount : receiptData.totalToPay;

    if (!requestAmount) {
      feeGroup.patchValue({ amount: 0, porcentualAmount: 0 });
      return;
    }

    // Seguir la misma lógica que income-receipt
    if (requestAmount.includes('%')) {
      // Es porcentaje
      const percentage = parseFloat(requestAmount.replace('%', '').trim());
      if (!isNaN(percentage)) {
        const amount = (percentage / 100) * originalAmount;
        feeGroup.patchValue({
          amount: amount,
          porcentualAmount: percentage
        });
      }
    } else {
      // Es valor fijo
      const amount = parseFloat(requestAmount.trim());
      if (!isNaN(amount)) {
        const percentage = originalAmount > 0 ? (amount / originalAmount) * 100 : 0;
        feeGroup.patchValue({
          amount: amount,
          porcentualAmount: percentage
        });
      }
    }
  }

  /**
   * Calcula totales de un comprobante específico
   */
  calculateReceiptTotals(receiptIndex: number): void {
    const receiptGroup = this.selectedReceiptsArray.at(receiptIndex) as FormGroup;
    const feeControls = this.getReceiptFeeDetails(receiptIndex).controls;

    // Calcular total de fees
    const totalFees = feeControls.reduce((total: number, feeControl: any) => {
      const feeGroup = feeControl as FormGroup;
      return total + (feeGroup.get('amount')?.value || 0);
    }, 0);

    // Actualizar resumen global
    this.updateSummary();
  }

  /**
   * Verifica si un comprobante está seleccionado
   */
  isReceiptSelected(receipt: any, type: 'income' | 'expense'): boolean {
    return this.selectedReceipts.some(r =>
      r.id === receipt.id && r.type === type
    );
  }

  selectAllReceipts(): void {
    // Limpiar selecciones previas
    this.clearAllSelections();

    // Agregar todos los ingresos
    this.pendingIncomeReceipts.forEach(receipt => {
      const receiptWithType = { ...receipt, type: 'income' };
      this.selectedReceipts.push(receiptWithType);
      this.addReceiptToForm(receiptWithType);
    });

    // Agregar todos los egresos
    this.pendingExpenseVouchers.forEach(receipt => {
      const receiptWithType = { ...receipt, type: 'expense' };
      this.selectedReceipts.push(receiptWithType);
      this.addReceiptToForm(receiptWithType);
    });

    this.updateSummary();
    this.toastService.showToast('success', 'Todos los comprobantes seleccionados');
  }

  clearAllSelections(): void {
    this.selectedReceipts = [];
    // Limpiar también el FormArray
    this.selectedReceiptsArray.clear();
    this.updateSummary();
    this.toastService.showToast('success', 'Selección limpiada');
  }

  // === STEP 4: CONCILIACIÓN AUTOMÁTICA ===

  async performAutoReconciliation(): Promise<void> {
    if (this.selectedReceipts.length === 0) {
      this.toastService.showToast('warning', 'Debe seleccionar al menos un comprobante');
      return;
    }

    this.loadingService.show();

    try {
      // Primero crear la conciliación
      const reconciliationData = this.buildReconciliationRequest();
      const createResponse = await this.bankReconciliationService.createReconciliation(reconciliationData).toPromise();

      if (createResponse?.isSuccess) {
        const reconciliationId = createResponse.data.id;

        // Luego realizar conciliación automática
        const tolerance = this.reconciliationForm.get('autoReconciliationTolerance')?.value || 0.01;
        const autoResponse = await this.bankReconciliationService.autoReconcileByAmount(reconciliationId, tolerance).toPromise();

        if (autoResponse?.isSuccess) {
          this.autoReconcileResults = autoResponse.data;
          this.showAutoReconcileResults = true;
          this.completedReconciliation = { ...createResponse.data, autoResults: autoResponse.data };

          this.toastService.showToast('success',
            `Conciliación automática completada: ${autoResponse.data.successfullyReconciled}/${autoResponse.data.processedCount} comprobantes`);

          // Avanzar al paso 5
          this.goToStep(5);
        } else {
          this.toastService.showToast('error', autoResponse?.errorMessage || 'Error en conciliación automática');
        }
      } else {
        this.toastService.showToast('error', createResponse?.errorMessage || 'Error creando conciliación');
      }

    } catch (error) {
      console.error('Error in auto reconciliation:', error);
      this.toastService.showToast('error', 'Error realizando conciliación automática');
    } finally {
      this.loadingService.hide();
    }
  }

  // === STEP 5: FINALIZACIÓN ===

  async completeReconciliation(): Promise<void> {
    const idToComplete = this.completedReconciliation?.id || this.existingReconciliation?.id || this.reconciliationId;

    if (!idToComplete) {
      this.toastService.showToast('error', 'No hay conciliación para completar');
      return;
    }

    this.loadingService.show();

    try {
      const response = await this.bankReconciliationService.completeReconciliation(idToComplete).toPromise();

      if (response?.isSuccess) {
        this.isReconciliationCompleted = true;
        this.completedReconciliation = response.data;
        if (this.existingReconciliation) {
          this.existingReconciliation = response.data;
        }
        this.toastService.showToast('success', 'Conciliación completada exitosamente');
      } else {
        this.toastService.showToast('error', response?.errorMessage || 'Error completando conciliación');
      }

    } catch (error) {
      console.error('Error completing reconciliation:', error);
      this.toastService.showToast('error', 'Error completando conciliación');
    } finally {
      this.loadingService.hide();
    }
  }

  async downloadReconciliationReport(): Promise<void> {
    if (!this.completedReconciliation?.id) {
      this.toastService.showToast('error', 'No hay conciliación para generar reporte');
      return;
    }

    this.loadingService.show();

    try {
      const blob = await this.bankReconciliationService.generateReconciliationReport(this.completedReconciliation.id).toPromise();
      if (blob) {
        const filename = `ConciliacionBancaria_${this.completedReconciliation.id}_${new Date().toISOString().slice(0, 10)}.xlsx`;
        this.bankReconciliationService.downloadReport(blob, filename);
        this.toastService.showToast('success', 'Reporte descargado exitosamente');
      } else {
        this.toastService.showToast('error', 'No se pudo generar el reporte');
      }

    } catch (error) {
      console.error('Error downloading report:', error);
      this.toastService.showToast('error', 'Error descargando reporte');
    } finally {
      this.loadingService.hide();
    }
  }

  // === MÉTODOS AUXILIARES ===

  private buildReconciliationRequest(): BankReconciliationRequestDTO {
    const formValue = this.reconciliationForm.value;

    // 🆕 Construir detalles desde el FormArray con montos y fees individuales
    const details = this.selectedReceiptsArray.controls.map((control: any) => {
      const group = control as FormGroup;
      const receiptData = group.get('receiptData')?.value;
      const bankAmountRaw = group.get('bankAmount')?.value;
      const bankAmount = typeof bankAmountRaw === 'string' ? Number((bankAmountRaw || '').toString().replace(/[^0-9.-]/g, '')) : Number(bankAmountRaw || 0);
      const adjustmentReason = group.get('adjustmentReason')?.value;
      const notes = group.get('notes')?.value;
      const feeControls = (group.get('feeDetails') as FormArray).controls;

      // Extraer fees del FormArray
      const fees = feeControls.map((feeControl: any) => {
        const feeGroup = feeControl as FormGroup;
        const feeTypeIdRaw = feeGroup.get('feeTypeId')?.value;
        const amountRaw = feeGroup.get('amount')?.value;
        const porcentualRaw = feeGroup.get('porcentualAmount')?.value;
        return {
          feeTypeId: Number(feeTypeIdRaw || 0),
          requestAmount: feeGroup.get('requestAmount')?.value,
          description: feeGroup.get('description')?.value,
          amount: typeof amountRaw === 'string' ? Number((amountRaw || '').toString().replace(/[^0-9.-]/g, '')) : Number(amountRaw || 0),
          porcentualAmount: Number(porcentualRaw || 0)
        };
      });

      return {
        receiptType: receiptData.type === 'income' ? 'INCOME' : 'EXPENSE' as 'INCOME' | 'EXPENSE',
        incomeReceiptId: receiptData.type === 'income' ? receiptData.id : undefined,
        expenseVoucherId: receiptData.type === 'expense' ? receiptData.id : undefined,
        bankAmount: bankAmount,
        adjustmentReason: adjustmentReason,
        notes: notes,
        incomeFees: receiptData.type === 'income' ? fees : [],
        expenseFees: receiptData.type === 'expense' ? fees : []
      };
    });

    return {
      bankId: formValue.bankId,
      reconciliationType: formValue.reconciliationType,
      month: formValue.month,
      year: formValue.year,
      totalBankAmount: typeof formValue.totalBankAmount === 'string' ? Number((formValue.totalBankAmount || '').toString().replace(/[^0-9.-]/g, '')) : Number(formValue.totalBankAmount || 0),
      notes: formValue.notes,
      details,
      filters: {
        projectId: formValue.projectId || undefined,
        participantId: formValue.participantId || undefined,
        companyId: formValue.companyId || undefined,
        minAmount: formValue.minAmount || undefined,
        maxAmount: formValue.maxAmount || undefined,
        autoReconciliationTolerance: formValue.autoReconciliationTolerance || 0.01,
        onlyUnreconciled: true
      }
    };
  }

  private updateSummary(): void {
    this.reconciliationSummary.selectedCount = this.selectedReceipts.length;

    // 🆕 Calcular totales desde el FormArray (montos individuales del banco)
    let totalBankFromDetails = 0;
    let totalSelectedFromDetails = 0;
    let totalFeesFromDetails = 0;

    this.selectedReceiptsArray.controls.forEach((control: any) => {
      const group = control as FormGroup;
      const receiptData = group.get('receiptData')?.value;
      const bankAmountRaw = group.get('bankAmount')?.value;
      const bankAmount = typeof bankAmountRaw === 'string' ? Number((bankAmountRaw || '').toString().replace(/[^0-9.-]/g, '')) : Number(bankAmountRaw || 0);

      totalBankFromDetails += bankAmount;
      totalSelectedFromDetails += receiptData.type === 'income' ? receiptData.netAmount : receiptData.totalToPay;
      // Sumar fees de este comprobante
      const feesArray = group.get('feeDetails') as FormArray;
      const feesTotal = feesArray.controls.reduce((acc: number, fc: any) => {
        const amountVal = fc.get('amount')?.value;
        const amount = typeof amountVal === 'string' ? Number((amountVal || '').toString().replace(/[^0-9.-]/g, '')) : Number(amountVal || 0);
        return acc + (amount || 0);
      }, 0);
      totalFeesFromDetails += feesTotal;
    });

    this.reconciliationSummary.totalSelected = totalSelectedFromDetails;

    // Usar el monto global del banco si está configurado, sino usar la suma de los individuales
    const globalBankAmount = this.reconciliationForm.get('totalBankAmount')?.value || 0;
    const globalBankAmountNum = typeof globalBankAmount === 'string' ? Number((globalBankAmount || '').toString().replace(/[^0-9.-]/g, '')) : Number(globalBankAmount || 0);
    this.reconciliationSummary.totalBank = globalBankAmountNum > 0 ? globalBankAmountNum : totalBankFromDetails;

    // Diferencia final global = Banco - (Originales - Fees)
    this.reconciliationSummary.differenceAmount =
      this.reconciliationSummary.totalBank - (this.reconciliationSummary.totalSelected - totalFeesFromDetails);

    this.reconciliationSummary.differencePercentage = this.reconciliationSummary.totalBank > 0
      ? (Math.abs(this.reconciliationSummary.differenceAmount) / this.reconciliationSummary.totalBank) * 100
      : 0;
  }

  private async createReconciliation(): Promise<void> {
    // Este método no se usa directamente en el wizard, 
    // la creación se maneja en performAutoReconciliation
    await this.performAutoReconciliation();
  }

  // === MÉTODOS DE DISPLAY ===

  getReconciliationTypeDisplayName(type?: string): string {
    const actualType = type || this.reconciliationType;
    switch (actualType) {
      case BankReconciliationType.INCOME:
        return 'Conciliación de Ingresos';
      case BankReconciliationType.EXPENSE:
        return 'Conciliación de Egresos';
      case BankReconciliationType.BOTH:
        return 'Conciliación Completa';
      default:
        return 'Tipo no definido';
    }
  }

  getSelectedBankName(): string {
    const bankId = this.reconciliationForm.get('bankId')?.value;
    const bank = this.banks.find(b => b.value === bankId);
    return bank?.label || 'Sin banco seleccionado';
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  openVoucher(receiptType: string, voucherId: number | undefined): void {
    if (!voucherId) return;
    const route = receiptType === 'INCOME' ? '/income-receipts/view' : '/expense-receipts/view';
    const url = this.router.serializeUrl(
      this.router.createUrlTree([route, voucherId])
    );
    window.open(url, '_blank');
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('es-CO');
  }

  // === MÉTODOS PARA DROPDOWNS ===

  getMonthOptions(): any[] {
    return [
      { label: 'Enero', value: 1 },
      { label: 'Febrero', value: 2 },
      { label: 'Marzo', value: 3 },
      { label: 'Abril', value: 4 },
      { label: 'Mayo', value: 5 },
      { label: 'Junio', value: 6 },
      { label: 'Julio', value: 7 },
      { label: 'Agosto', value: 8 },
      { label: 'Septiembre', value: 9 },
      { label: 'Octubre', value: 10 },
      { label: 'Noviembre', value: 11 },
      { label: 'Diciembre', value: 12 }
    ];
  }

  getYearOptions(): any[] {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2020; year--) {
      years.push({ label: year.toString(), value: year });
    }
    return years;
  }

  getSelectedPeriodDisplay(): string {
    const month = this.reconciliationForm.get('month')?.value;
    const year = this.reconciliationForm.get('year')?.value;

    if (!month || !year) return 'Sin período seleccionado';

    const monthName = this.getMonthOptions().find(m => m.value === month)?.label || '';
    return `${monthName} ${year}`;
  }

  // === MÉTODOS AUXILIARES PARA FEES Y TOTALES ===

  /**
   * Obtiene el total de fees de un comprobante específico
   */
  getReceiptTotalFees(receiptIndex: number): number {
    const feeControls = this.getReceiptFeeDetails(receiptIndex).controls;
    return feeControls.reduce((total: number, feeControl: any) => {
      const feeGroup = feeControl as FormGroup;
      return total + (feeGroup.get('amount')?.value || 0);
    }, 0);
  }

  /**
   * Obtiene la diferencia individual de un comprobante (banco - original)
   */
  getReceiptBankDifference(receiptIndex: number): number {
    const receiptGroup = this.selectedReceiptsArray.at(receiptIndex) as FormGroup;
    const receiptData = receiptGroup.get('receiptData')?.value;
    const bankAmount = receiptGroup.get('bankAmount')?.value || 0;
    const originalAmount = receiptData.type === 'income' ? receiptData.netAmount : receiptData.totalToPay;

    return bankAmount - originalAmount;
  }

  /**
   * Obtiene la diferencia final de un comprobante (después de fees)
   */
  getReceiptFinalDifference(receiptIndex: number): number {
    const bankDifference = this.getReceiptBankDifference(receiptIndex);
    const totalFees = this.getReceiptTotalFees(receiptIndex);

    // Diferencia final: Banco - (Original - Fees) = (Banco - Original) + Fees
    return bankDifference + totalFees;
  }

  /**
   * Obtiene el monto conciliado final de un comprobante
   */
  getReceiptReconciledAmount(receiptIndex: number): number {
    const receiptGroup = this.selectedReceiptsArray.at(receiptIndex) as FormGroup;
    const receiptData = receiptGroup.get('receiptData')?.value;
    const originalAmount = receiptData.type === 'income' ? receiptData.netAmount : receiptData.totalToPay;
    const totalFees = this.getReceiptTotalFees(receiptIndex);

    // El monto conciliado esperado tras fees (descuentos) es el original menos los fees
    return originalAmount - totalFees;
  }

  /**
   * Indica si un comprobante está balanceado
   */
  isReceiptBalanced(receiptIndex: number): boolean {
    return Math.abs(this.getReceiptFinalDifference(receiptIndex)) <= 0.01;
  }

  // === NAVEGACIÓN Y CANCELACIÓN ===

  async onCancel(): Promise<void> {
    await this.handleFormCancel('/bank-reconciliation');
  }

  goToReconciliationsList(): void {
    this.router.navigate(['/bank-reconciliation']);
  }

  async createAnotherReconciliation(): Promise<void> {
    const canReset = await this.confirmationService.confirmFormReset('la conciliación bancaria');
    if (!canReset) return;

    // Resetear formulario y estado
    this.reconciliationForm.reset();
    this.initializeForm(); // Esto ya inicializa selectedReceiptsForm
    this.clearDependentData();
    this.completedReconciliation = null;
    this.isReconciliationCompleted = false;
    this.step = 1;

    this.toastService.showToast('success', 'Formulario reiniciado para nueva conciliación');
  }

  // === 🆕 MÉTODOS PARA MODAL DE CONFIGURACIÓN INDIVIDUAL ===

  openReceiptConfigModal(receiptIndex: number): void {
    this.currentReceiptIndex = receiptIndex;
    this.currentReceiptForModal = this.selectedReceipts[receiptIndex];
    this.showReceiptConfigModal = true;
  }

  closeReceiptConfigModal(): void {
    this.showReceiptConfigModal = false;
    this.currentReceiptIndex = -1;
    this.currentReceiptForModal = null;
  }

  onReceiptConfigSave(configData: any): void {
    // Los datos ya están actualizados en el FormArray
    this.updateSummary();
    this.toastService.showToast('success', 'Configuración guardada');
  }

  navigateToReceiptInModal(receiptIndex: number): void {
    if (receiptIndex >= 0 && receiptIndex < this.selectedReceipts.length) {
      this.currentReceiptIndex = receiptIndex;
      this.currentReceiptForModal = this.selectedReceipts[receiptIndex];
    }
  }

  onReceiptFeeChanged(event: { receiptIndex: number, feeIndex: number }): void {
    this.calculateReceiptTotals(event.receiptIndex);
  }

  // === 🆕 MÉTODOS PARA MODAL DE CONFIGURACIÓN POR LOTES ===

  openBatchConfigModal(): void {
    if (this.selectedReceipts.length === 0) {
      this.toastService.showToast('warning', 'Debe seleccionar al menos un comprobante');
      return;
    }
    this.showBatchConfigModal = true;
  }

  closeBatchConfigModal(): void {
    this.showBatchConfigModal = false;
  }

  onBatchConfigApply(config: BatchConfigOptions): void {
    this.applyBatchConfiguration(config);
    this.closeBatchConfigModal();
  }

  private applyBatchConfiguration(config: BatchConfigOptions): void {
    let appliedCount = 0;

    this.selectedReceiptsArray.controls.forEach((control: any, index: number) => {
      const group = control as FormGroup;
      const receiptData = group.get('receiptData')?.value;
      const originalAmount = receiptData.type === 'income' ? receiptData.netAmount : receiptData.totalToPay;

      // Aplicar monto del banco
      if (config.applyBankAmount) {
        let bankAmount = 0;

        // El valor viene calculado desde el modal según el modo seleccionado
        if (config.bankAmountValue === 1) {
          // Modo original
          bankAmount = originalAmount;
        } else if (config.bankAmountValue < 1) {
          // Modo porcentaje
          bankAmount = originalAmount * config.bankAmountValue;
        } else {
          // Modo fijo
          bankAmount = config.bankAmountValue;
        }

        group.patchValue({ bankAmount: bankAmount });
      }

      // Aplicar fees
      if (config.applyFees && config.selectedFees.length > 0) {
        const feeDetailsArray = group.get('feeDetails') as FormArray;

        // Limpiar fees existentes si es necesario
        feeDetailsArray.clear();

        // Agregar nuevos fees
        config.selectedFees.forEach(fee => {
          const feeGroup = this.fb.group({
            feeTypeId: [fee.feeTypeId, Validators.required],
            requestAmount: [fee.requestAmount, [Validators.required]],
            description: [fee.description],
            amount: [0],
            porcentualAmount: [0]
          });

          feeDetailsArray.push(feeGroup);

          // Calcular el monto del fee
          this.calculateFeeAmountForBatch(feeGroup, originalAmount, fee.requestAmount);
        });
      }

      // Aplicar razón de ajuste
      if (config.applyAdjustmentReason) {
        group.patchValue({ adjustmentReason: config.adjustmentReasonValue });
      }

      // Aplicar notas
      if (config.applyNotes) {
        const currentNotes = group.get('notes')?.value || '';
        const newNotes = config.notesValue;

        // Modo de aplicación: replace o append
        const finalNotes = currentNotes && newNotes ?
          `${currentNotes}\n${newNotes}` :
          newNotes;

        group.patchValue({ notes: finalNotes });
      }

      appliedCount++;
    });

    // Recalcular totales
    this.updateSummary();

    this.toastService.showToast('success',
      `Configuración aplicada a ${appliedCount} comprobantes`);
  }

  private calculateFeeAmountForBatch(feeGroup: FormGroup, originalAmount: number, requestAmount: string): void {
    if (!requestAmount) {
      feeGroup.patchValue({ amount: 0, porcentualAmount: 0 });
      return;
    }

    if (requestAmount.includes('%')) {
      // Es porcentaje
      const percentage = parseFloat(requestAmount.replace('%', '').trim());
      if (!isNaN(percentage)) {
        const amount = (percentage / 100) * originalAmount;
        feeGroup.patchValue({
          amount: amount,
          porcentualAmount: percentage
        });
      }
    } else {
      // Es valor fijo
      const amount = parseFloat(requestAmount.trim());
      if (!isNaN(amount)) {
        const percentage = originalAmount > 0 ? (amount / originalAmount) * 100 : 0;
        feeGroup.patchValue({
          amount: amount,
          porcentualAmount: percentage
        });
      }
    }
  }

  // === 🆕 MÉTODOS PARA INDICADORES VISUALES DE ESTADO ===

  getReceiptConfigurationStatus(receiptIndex: number): 'complete' | 'partial' | 'empty' {
    const receiptGroup = this.selectedReceiptsArray.at(receiptIndex) as FormGroup;
    if (!receiptGroup) return 'empty';

    const bankAmount = receiptGroup.get('bankAmount')?.value;
    const hasValidBankAmount = bankAmount && bankAmount > 0;

    if (hasValidBankAmount && this.isReceiptBalanced(receiptIndex)) return 'complete';
    if (hasValidBankAmount) return 'partial';
    return 'empty';
  }

  getReceiptStatusIcon(receiptIndex: number): string {
    const status = this.getReceiptConfigurationStatus(receiptIndex);
    switch (status) {
      case 'complete': return '✅';
      case 'partial': return '⚠️';
      case 'empty': return '⏳';
      default: return '⏳';
    }
  }

  getReceiptStatusText(receiptIndex: number): string {
    const status = this.getReceiptConfigurationStatus(receiptIndex);
    switch (status) {
      case 'complete': return 'Configurado y Balanceado';
      case 'partial': return 'Configurado con Diferencias';
      case 'empty': return 'Pendiente de Configurar';
      default: return 'Sin Configurar';
    }
  }

  getReceiptStatusColor(receiptIndex: number): string {
    const status = this.getReceiptConfigurationStatus(receiptIndex);
    switch (status) {
      case 'complete': return 'text-green-600';
      case 'partial': return 'text-yellow-600';
      case 'empty': return 'text-gray-400';
      default: return 'text-gray-400';
    }
  }

  // === 🆕 MÉTODOS DE UTILIDAD PARA LA NUEVA UX ===

  getConfiguredReceiptsCount(): number {
    let count = 0;
    for (let i = 0; i < this.selectedReceiptsArray.length; i++) {
      const status = this.getReceiptConfigurationStatus(i);
      if (status === 'complete' || status === 'partial') {
        count++;
      }
    }
    return count;
  }

  getBalancedReceiptsCount(): number {
    let count = 0;
    for (let i = 0; i < this.selectedReceiptsArray.length; i++) {
      if (this.isReceiptBalanced(i)) {
        count++;
      }
    }
    return count;
  }

  getAllReceiptsConfigured(): boolean {
    return this.getConfiguredReceiptsCount() === this.selectedReceipts.length;
  }

  // === 🆕 MÉTODOS AUXILIARES PARA EL TEMPLATE (evitar arrow functions) ===

  getReceiptIndexInSelected(receipt: any, type: 'income' | 'expense'): number {
    return this.selectedReceipts.findIndex(r => r.id === receipt.id && r.type === type);
  }

  getReceiptStatusIconForTable(receipt: any, type: 'income' | 'expense'): string {
    const index = this.getReceiptIndexInSelected(receipt, type);
    return index >= 0 ? this.getReceiptStatusIcon(index) : '⏳';
  }

  getReceiptStatusColorForTable(receipt: any, type: 'income' | 'expense'): string {
    const index = this.getReceiptIndexInSelected(receipt, type);
    return index >= 0 ? this.getReceiptStatusColor(index) : 'text-gray-400';
  }

  getReceiptStatusTextForTable(receipt: any, type: 'income' | 'expense'): string {
    const index = this.getReceiptIndexInSelected(receipt, type);
    const fullText = index >= 0 ? this.getReceiptStatusText(index) : 'Sin Configurar';
    return fullText.split(' ')[0]; // Solo la primera palabra
  }

  openReceiptConfigModalForTable(receipt: any, type: 'income' | 'expense'): void {
    const index = this.getReceiptIndexInSelected(receipt, type);
    if (index >= 0) {
      this.openReceiptConfigModal(index);
    }
  }

  // === MÉTODO AUXILIAR PARA EL TEMPLATE ===

  getCurrentReceiptFormGroup(): FormGroup | null {
    if (this.currentReceiptIndex >= 0 && this.currentReceiptIndex < this.selectedReceiptsArray.length) {
      return this.selectedReceiptsArray.at(this.currentReceiptIndex) as FormGroup;
    }
    return null;
  }

  // === MÉTODOS DE MODAL DE CONFIRMACIÓN ===

  onConfirmationModalConfirmed(): void {
    this.onModalConfirmed();
  }

  onConfirmationModalCancelled(): void {
    this.onModalCancelled();
  }

  onConfirmationModalClosed(): void {
    this.onModalClosed();
  }

  // === VIEW MODE HELPERS ===

  getStatusDisplayName(status: string | undefined): string {
    if (!status) return 'Desconocido';
    switch (status) {
      case 'IN_PROGRESS': return 'En Progreso';
      case 'COMPLETED': return 'Completada';
      case 'CANCELLED': return 'Cancelada';
      default: return status;
    }
  }

  getStatusBadgeClass(status: string | undefined): string {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status) {
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }
}
